import React, { useEffect, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { makeStyles } from "@material-ui/core/styles";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import classNames from "classnames";
import ErrorMessage from "./common/ErrorMessage";
import { getError } from "../../utils/validations";

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginBottom: theme.spacing(1),
    minWidth: "100%",
    width: "100%",
  },
  label: {
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
    fontSize: "1rem",
  },
  optionContainer: {
    marginBottom: theme.spacing(1),
    display: "flex",
    justifyContent: "center",
    height: "3rem",
    position: "relative",
    cursor: "pointer",
    margin: 0,
  },
  button: {
    color: theme.palette.button.main,
  },
  controlLabel: {
    width: "50%",
    height: "2.8rem",
    justifyContent: "center",
    margin: "0",
    "&:nth-child(even)": {
      borderRadius: "0px 5px 5px 0px",
      border: "1px solid #ced4da",
    },
    "&:nth-child(odd)": {
      borderRadius: "5px 0px 0px 5px",
      border: "1px solid #ced4da",
      borderRight: "0px",
    },
    "&.checked": {
      background: theme.palette.button.main,
      "& > .MuiFormControlLabel-label": {
        color: theme.palette.primary.light,
      }
    },
    "&:hover": {
      background: theme.palette.button.main,
      opacity: "0.5",
      "& > .MuiFormControlLabel-label": {
        color: theme.palette.primary.light,
      }
    },
    "& span.MuiIconButton-root": {
      position: "absolute",
      color: "transparent",
    },
    "& span.Mui-focusVisible": {
      boxShadow: "1px 1px 5px black",
    },
  },
}));

const ButtonField = ({ name, label, choices, validations }) => {
  const classes = useStyles();
  const {
    register,
    unregister,
    watch,
    getValues,
    setValue,
    submit,
  } = useFormContext();
  const watchValue = watch(name);

  useEffect(() => {
    register(name, getError({ validations }));

    return () => {
      unregister(name, validations);
    };
  }, [name, validations, register, unregister]);

  const handleChange = useCallback(
    (e) => {
      setValue(name, e.target.value);

      const isOnlyField = Object.keys(getValues()).length === 1;

      if (isOnlyField) {
        submit();
      }
    },
    [name, setValue, getValues, submit]
  );

  return (
    <FormControl className={classes.formControl}>
      <label htmlFor={name}>{label}</label>
      <RadioGroup
        aria-label={label}
        name={label}
        className={classes.optionContainer}
        onChange={handleChange}
      >
        {choices.map(({ key, text }) => {
          const labelClass = classNames(classes.controlLabel, {
            checked: watchValue === key,
          });
          return (
            <FormControlLabel
              value={key}
              key={key}
              className={labelClass}
              control={
                <Radio name={name} label={text} className={classes.button} />
              }
              label={text}
            />
          );
        })}
      </RadioGroup>
      <ErrorMessage name={name} />
    </FormControl>
  );
};

export default ButtonField;
